import { ChatAttachmentService } from '@agent/agent-interface/contact-view/chat/services/chat-attachment.service';
import { Injector } from '@angular/core';
import { ChatAssistanceService } from '@commons/chat-assistance/chat-assistance.service';
import { NG1_CIRCUIT, NG1_IADVIZE, NG1_OSV, NG1_SKYPE, NG1_VIDEOCONFERENCE } from '@commons/core/core.provider.ajs';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { RxUtil } from '@commons/util/utilities/rx';
import { combineLatest, filter, map, of, pipe, switchMap, tap, withLatestFrom } from 'rxjs';

import {
  AgentStatus,
  AgentSystemFacade,
  AgentUserFacade,
  fTruthy,
  ProfileService,
  TILE_TRANSITION_CONDITIONS,
  TileId,
  TileTransitionCondition,
  TileTransitionPhase,
  TranslateService,
} from '@contact/upgrade';

import { IAdvizeService } from './core/channel-digit/chat-iadvize/iadvize.service';
import { CircuitService } from './core/channel-digit/softphone/circuit/circuit.service';
import { OsvService } from './core/channel-digit/softphone/osv/osv.service';
import { SkypeService } from './core/channel-digit/softphone/skype/skype.service';
import { VideoconferenceService } from './core/channel-digit/videoconference/videoconference.service';

export const CRM_FRAME_NAME = 'CRM';

const appStatus = (agentSystemFacade: AgentSystemFacade) => withLatestFrom(agentSystemFacade.ready$, (_, ready) => ready);
const appReady = (agentSystemFacade: AgentSystemFacade) => pipe(appStatus(agentSystemFacade), fTruthy());

function AgentStatusNavGuard(agentSystemFacade: AgentSystemFacade, agentUserFacade: AgentUserFacade): TileTransitionCondition {
  // current status
  const status$ = agentUserFacade.status$;
  const notWorking$ = status$.pipe(map(status => status === AgentStatus.NOT_WORKING));
  const connected$ = status$.pipe(map(status => status === AgentStatus.CONNECTED));

  // adding navigation condition
  const canNavigate$ = combineLatest(notWorking$, connected$, (notWorking, connected) => notWorking || connected);
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.CAN || phase === TileTransitionPhase.GUARD),
      filter(({ from, to }) => !!from && from.id === TileId.CSR && !!to),
      appReady(agentSystemFacade),
      switchMap(() => canNavigate$)
    );
}

function AgentStatusLogoutGuard(
  agentSystemFacade: AgentSystemFacade,
  agentUserFacade: AgentUserFacade,
  profileService: ProfileService
): TileTransitionCondition {
  // current status
  const status$ = agentUserFacade.status$;
  const working$ = status$.pipe(map(status => status === AgentStatus.WORKING));
  const logoutForbidden$ = profileService.hasPermission$('CSR::self:working_logout_forbidden');

  // adding navigation condition
  const canLogout$ = combineLatest(working$, logoutForbidden$, (working, logoutForbidden) => !(working && logoutForbidden));
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.CAN || phase === TileTransitionPhase.GUARD),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      switchMap(() => canLogout$)
    );
}

function ChatAttachmentGuard(
  agentSystemFacade: AgentSystemFacade,
  chatAttachmentService: ChatAttachmentService,
  translateService: TranslateService
): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.GUARD),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      map(() => {
        if (!chatAttachmentService.canLogout()) {
          return window.confirm(translateService.instant('agent:panel.chat.attachment.confirm_message'));
        }
        return true;
      })
    );
}

function ChatAssistanceResolver(
  agentSystemFacade: AgentSystemFacade,
  chatAssistanceService: ChatAssistanceService
): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      tap(() => chatAssistanceService.disconnect())
    );
}

function AgentReducedResolver(agentSystemFacade: AgentSystemFacade, appExternalService: AppExternalService): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      tap(() => appExternalService.removeApplication(CRM_FRAME_NAME))
    );
}

function IadvizeResolver(agentSystemFacade: AgentSystemFacade, injector: Injector): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      switchMap(() => RxUtil.fromPromise(() => (injector.get(NG1_IADVIZE) as IAdvizeService).close()))
    );
}

function CircuitResolver(agentSystemFacade: AgentSystemFacade, injector: Injector): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      switchMap(() => RxUtil.fromPromise(() => (injector.get(NG1_CIRCUIT) as CircuitService).logout()))
    );
}

function OsvResolver(agentSystemFacade: AgentSystemFacade, injector: Injector): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      switchMap(() => RxUtil.fromPromise(() => (injector.get(NG1_OSV) as OsvService).logout()))
    );
}

function SkypeResolver(agentSystemFacade: AgentSystemFacade, injector: Injector): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      switchMap(() => RxUtil.fromPromise(() => (injector.get(NG1_SKYPE) as SkypeService).logout()))
    );
}

function VideoconferenceResolver(agentSystemFacade: AgentSystemFacade, injector: Injector): TileTransitionCondition {
  return transition =>
    of(transition).pipe(
      filter(({ phase }) => phase === TileTransitionPhase.RESOLVER),
      filter(({ to }) => !to),
      appReady(agentSystemFacade),
      tap(() => (injector.get(NG1_VIDEOCONFERENCE) as VideoconferenceService).closeConsole())
    );
}

const AgentStatusNavProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: AgentStatusNavGuard,
  deps: [AgentSystemFacade, AgentUserFacade],
  multi: true,
};

const AgentStatusLogoutProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: AgentStatusLogoutGuard,
  deps: [AgentSystemFacade, AgentUserFacade, ProfileService],
  multi: true,
};

const ChatAttachmentProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: ChatAttachmentGuard,
  deps: [AgentSystemFacade, ChatAttachmentService, TranslateService],
  multi: true,
};

const ChatAssistanceProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: ChatAssistanceResolver,
  deps: [AgentSystemFacade, ChatAssistanceService],
  multi: true,
};

const AgentReducedProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: AgentReducedResolver,
  deps: [AgentSystemFacade, AppExternalService],
  multi: true,
};

const IadvizeProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: IadvizeResolver,
  deps: [AgentSystemFacade, Injector],
  multi: true,
};

const CircuitProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: CircuitResolver,
  deps: [AgentSystemFacade, Injector],
  multi: true,
};

const OsvProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: OsvResolver,
  deps: [AgentSystemFacade, Injector],
  multi: true,
};

const SkypeProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: SkypeResolver,
  deps: [AgentSystemFacade, Injector],
  multi: true,
};

const VideoconferenceProvider = {
  provide: TILE_TRANSITION_CONDITIONS,
  useFactory: VideoconferenceResolver,
  deps: [AgentSystemFacade, Injector],
  multi: true,
};

export const AGENT_GUARDS = [
  AgentStatusNavProvider,
  AgentStatusLogoutProvider,
  ChatAttachmentProvider,
  ChatAssistanceProvider,
  AgentReducedProvider,
  IadvizeProvider,
  CircuitProvider,
  OsvProvider,
  SkypeProvider,
  VideoconferenceProvider,
];
